import React, { useEffect, useState, useRef } from "react";
import Sidebar from "./Sidebar";
import SavedCard from "./SavedCard";
import ProgramCard from "./ProgramCard";
import SchoolContactConsentModal from "./SchoolContactConsentModal";

import api from "../axiosConfig";
import {
  getAuthSavedPageCacheSnapshot,
  writeSavedPageCache,
} from "../utils/savedPageCache";

const Saved = () => {
  const [sideNavOpen, setSideNavOpen] = useState(true);

  const [initialSnap] = useState(() => getAuthSavedPageCacheSnapshot());
  const hadCacheOnMount = useRef(!!initialSnap);

  const [savedProfessors, setSavedProfessors] = useState(
    () => initialSnap?.savedProfessors ?? []
  );
  const [allProfessorsSeen, setAllProfessorsSeen] = useState(
    () => initialSnap?.allProfessorsSeen ?? []
  );
  const [schoolsOfSavedProfessors, setSchoolsOfSavedProfessors] = useState(
    () => initialSnap?.schoolsOfSavedProfessors ?? []
  );
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const [loading, setLoading] = useState(() => ({
    savedProfessors: !initialSnap,
    allProfessorsSeen: !initialSnap,
    schoolsOfSavedProfessors: !initialSnap,
  }));

  const cacheSnapshotRef = useRef({});

  const [showSchoolContactModal, setShowSchoolContactModal] = useState(false);
  const [allowSchoolContact, setAllowSchoolContact] = useState(false);
  const [schoolContactPromptAcknowledged, setSchoolContactPromptAcknowledged] =
    useState(false);
  const [savedProfessorCountFromPrefs, setSavedProfessorCountFromPrefs] =
    useState(0);
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  const fetchSavedProfessors = async (options = {}) => {
    const silent = !!options.silent;
    if (!silent) {
      setLoading((prev) => ({ ...prev, savedProfessors: true }));
    }
    try {
      const response = await api.get("/get-saved-professors/");
      const professorsWithDates = response.data.map((prof) => ({
        ...prof,
        saved_date: new Date(prof.saved_date),
      }));
      setSavedProfessors(professorsWithDates);
    } catch (error) {
      console.error("Error fetching saved professors:", error);
      if (!silent) {
        setErrorMessage("Failed to load saved professors.");
      }
    } finally {
      if (!silent) {
        setLoading((prev) => ({ ...prev, savedProfessors: false }));
      }
    }
  };

  const fetchAllProfessorsSeen = async (options = {}) => {
    const silent = !!options.silent;
    if (!silent) {
      setLoading((prev) => ({ ...prev, allProfessorsSeen: true }));
    }
    try {
      const response = await api.get("/get-seen-professors/?excludeSaved=true");
      const professorsWithDates = response.data.map((prof) => ({
        ...prof,
        seen_date: new Date(prof.seen_date),
      }));
      setAllProfessorsSeen(professorsWithDates);
    } catch (error) {
      console.error("Error fetching all professors seen:", error);
      if (!silent) {
        setErrorMessage("Failed to fetch recently seen professors.");
      }
    } finally {
      if (!silent) {
        setLoading((prev) => ({ ...prev, allProfessorsSeen: false }));
      }
    }
  };

  const fetchSchoolsOfSavedProfessors = async (options = {}) => {
    const silent = !!options.silent;
    if (!silent) {
      setLoading((prev) => ({ ...prev, schoolsOfSavedProfessors: true }));
    }
    try {
      const response = await api.get("/get-school-recommendations/");
      setSchoolsOfSavedProfessors(response.data);
    } catch (error) {
      console.error("Error fetching schools of saved professors:", error);
      if (!silent) {
        setErrorMessage("Failed to fetch school recommendations.");
      }
    } finally {
      if (!silent) {
        setLoading((prev) => ({ ...prev, schoolsOfSavedProfessors: false }));
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      setErrorMessage("You must be logged in to view this page.");
      return;
    }

    const silent = hadCacheOnMount.current;
    fetchSavedProfessors({ silent });
    fetchAllProfessorsSeen({ silent });
    fetchSchoolsOfSavedProfessors({ silent });
  }, []);

  // School contact consent: first visit to Saved after saving at least one professor
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/user-preferences/");
        if (cancelled) return;
        setAllowSchoolContact(!!data.allow_school_contact);
        setSchoolContactPromptAcknowledged(
          !!data.school_contact_prompt_acknowledged
        );
        setSavedProfessorCountFromPrefs(data.saved_professor_count ?? 0);
      } catch (e) {
        console.error("user-preferences", e);
      } finally {
        if (!cancelled) setPrefsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!prefsLoaded) return;
    if (
      savedProfessorCountFromPrefs > 0 &&
      !allowSchoolContact &&
      !schoolContactPromptAcknowledged
    ) {
      setShowSchoolContactModal(true);
    }
  }, [
    prefsLoaded,
    savedProfessorCountFromPrefs,
    allowSchoolContact,
    schoolContactPromptAcknowledged,
  ]);

  const patchSchoolContactPrefs = async (body) => {
    const { data } = await api.patch("/user-preferences/", body);
    setAllowSchoolContact(!!data.allow_school_contact);
    setSchoolContactPromptAcknowledged(
      !!data.school_contact_prompt_acknowledged
    );
    setSavedProfessorCountFromPrefs(data.saved_professor_count ?? 0);
    setShowSchoolContactModal(false);
  };

  const confirmSchoolContactShare = async () => {
    try {
      await patchSchoolContactPrefs({
        allow_school_contact: true,
        school_contact_prompt_acknowledged: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const notNowSchoolContact = async () => {
    try {
      await patchSchoolContactPrefs({
        school_contact_prompt_acknowledged: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    return () => {
      const s = cacheSnapshotRef.current;
      writeSavedPageCache({
        savedProfessors: s.savedProfessors,
        allProfessorsSeen: s.allProfessorsSeen,
        schoolsOfSavedProfessors: s.schoolsOfSavedProfessors,
      });
    };
  }, []);

  const handleExpand = (id) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const toggleSaved = (professorId) => {
    if (savedProfessors.some((professor) => professor.id === professorId)) {
      unmarkSaved(professorId);
    } else {
      markSaved(professorId);
    }
  };

  const markSaved = async (professorId) => {
    try {
      await api.post("/handle-interaction/", {
        professor_id: professorId,
        interaction_type: "saved",
      });
      await fetchSavedProfessors({ silent: true });
      await fetchSchoolsOfSavedProfessors({ silent: true });
    } catch (error) {
      console.error("Error saving professor:", error);
    }
  };

  const unmarkSaved = async (professorId) => {
    try {
      await api.post("/unmark-saved/", { professor_id: professorId });
      await fetchSavedProfessors({ silent: true });
      await fetchSchoolsOfSavedProfessors({ silent: true });
    } catch (error) {
      console.error("Error unsaving professor:", error);
    }
  };

  cacheSnapshotRef.current = {
    savedProfessors,
    allProfessorsSeen,
    schoolsOfSavedProfessors,
  };

  return (
    <div className="flex flex-col nav:flex-row h-screen">
      <Sidebar
        sideNavOpen={sideNavOpen}
        toggleSideNav={() => setSideNavOpen(!sideNavOpen)}
      />
      <div className="flex-1 flex flex-col overflow-x-hidden p-4 nav:p-6 px-4 nav:px-8 min-h-0 overflow-y-auto">
        <h1 className="text-2xl nav:text-3xl font-semibold">Saved Professors</h1>
        {errorMessage && (
          <p className="error-message text-red-500 mt-4">{errorMessage}</p>
        )}

        {/* Saved Professors Section */}
        <div className="saved-professors-section mt-6 nav:mt-8">
          {loading.savedProfessors ? (
            <p>Loading saved professors...</p>
          ) : savedProfessors.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {savedProfessors.map((professor) => (
                <SavedCard
                  key={professor.id}
                  professor={professor}
                  type="saved"
                  isExpanded={expandedCardId === professor.id}
                  onExpand={handleExpand}
                  isSaved={professor.saved}
                  onSaved={() => toggleSaved(professor.id)}
                />
              ))}
            </div>
          ) : (
            <p>No saved professors yet.</p>
          )}
        </div>

        {/* Schools of Saved Professors Section */}
        <div className="schools-saved-professors-section mt-10 nav:mt-12">
          <h2 className="text-xl nav:text-2xl font-semibold mb-4">
            Schools You&apos;ve Saved Professors From
          </h2>
          {loading.schoolsOfSavedProfessors ? (
            <p>Loading school data...</p>
          ) : schoolsOfSavedProfessors.length > 0 ? (
            <ProgramCard schools={schoolsOfSavedProfessors} />
          ) : (
            <p>No schools found for saved professors.</p>
          )}
        </div>

        {/* Seen Professors Section */}
        <div className="all-professors-seen-section mt-6 nav:mt-8">
          <h2 className="text-xl nav:text-2xl font-semibold mb-4">Recently Seen</h2>
          {loading.allProfessorsSeen ? (
            <p>Loading professors seen...</p>
          ) : allProfessorsSeen.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {allProfessorsSeen.slice(0, 6).map((professor) => (
                <SavedCard
                  key={professor.id}
                  professor={professor}
                  type="seen"
                  isExpanded={expandedCardId === professor.id}
                  onExpand={handleExpand}
                  isSaved={savedProfessors.some((p) => p.id === professor.id)}
                  onSaved={() => toggleSaved(professor.id)}
                />
              ))}
            </div>
          ) : (
            <p>No professors viewed yet.</p>
          )}
        </div>
      </div>

      <SchoolContactConsentModal
        open={showSchoolContactModal}
        onConfirmShare={confirmSchoolContactShare}
        onSaveWithoutSharing={notNowSchoolContact}
      />
    </div>
  );
};

export default Saved;
